import React, { useEffect } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Lightweight image attachments bar that listens for clipboard pastes
// and renders small removable previews. Encodes images as data URLs.
//
// Props:
// - value: array of { id, name, type, size, dataUrl, base64 }
// - onChange: (nextArray) => void
// - maxImages: number (default 8)
// - maxSizeMB: number (default 8)
// - sx: style overrides for the container
export default function ImageAttachments({ value = [], onChange, maxImages = 8, maxSizeMB = 8, sx }) {
  useEffect(() => {
    const handlePaste = (event) => {
      const clipboardData = event.clipboardData || window.clipboardData;
      if (!clipboardData || !clipboardData.items) return;

      const items = Array.from(clipboardData.items);
      const imageItems = items.filter((item) => item.kind === 'file' && item.type && item.type.startsWith('image/'));
      if (imageItems.length === 0) return;

      // Prevent default paste so images aren't inserted as text
      event.preventDefault();

      const remainingSlots = Math.max(0, maxImages - value.length);
      const toTake = imageItems.slice(0, remainingSlots);
      if (toTake.length === 0) return;

      toTake.forEach((item) => {
        const file = item.getAsFile();
        if (!file) return;
        const sizeLimit = maxSizeMB * 1024 * 1024;
        if (file.size > sizeLimit) return; // silently skip oversize

        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result;
          const base64 = typeof dataUrl === 'string' && dataUrl.includes(',') ? dataUrl.split(',')[1] : null;
          const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
          const name = file.name || `pasted-${id}.png`;
          const next = [
            ...value,
            {
              id,
              name,
              type: file.type || 'image/png',
              size: file.size,
              dataUrl,
              base64
            }
          ];
          onChange?.(next);
        };
        reader.readAsDataURL(file);
      });
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, onChange, maxImages, maxSizeMB]);

  const handleRemove = (id) => {
    const next = value.filter((img) => img.id !== id);
    onChange?.(next);
  };

  if (!value || value.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1, ...sx }}>
      {value.map((img) => (
        <Box
          key={img.id}
          sx={{
            position: 'relative',
            width: 48,
            height: 48,
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          <img src={img.dataUrl} alt={img.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <Tooltip title="Remove">
            <IconButton
              size="small"
              onClick={() => handleRemove(img.id)}
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                bgcolor: 'background.paper',
                boxShadow: 1,
                '&:hover': { bgcolor: 'background.paper' }
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
      {value.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
          {value.length} image{value.length > 1 ? 's' : ''} attached
        </Typography>
      )}
    </Box>
  );
}
